// File: src/components/mixer/MixerPortalHeader.jsx
import React, { useEffect, useMemo, useState } from 'react';

const styles = {
  container: {
    maxWidth: 600,
    width: '94%',
    margin: '0 auto',
    textAlign: 'center',
    padding: '8px 0 32px',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
    color: '#111827',
  },

  /* Headline block (match RevealIntroWizardPage) */
  headerWrap: {
    textAlign: 'center',
    maxWidth: 640,
    margin: '0 auto',
  },
  headerTop: {
    fontSize: 'clamp(1.15rem, 2.6vw, 1.05rem)',
    fontWeight: 900,
    letterSpacing: '0.06em',
    lineHeight: 1.15,
    margin: "-12px 0 0 0",
    color: '#000',
  },
  headerBottom: {
    fontSize: 'clamp(1.8rem, 4.2vw, 2.2rem)',
    lineHeight: 1.25,
    marginTop: 8,
    fontWeight: 900,
    letterSpacing: '-0.01em',
    color: '#0B1020',
  },
  subheading: {
    fontSize: 13.5,
    color: '#3A4050',
    lineHeight: 1.55,
    margin: '10px 28px 6px 28px',
  },

  /* Timer */
  timerContainer: {
    marginTop: '0.2rem',
    marginBottom: '1.25rem',
  },
  timerPill: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    padding: '.35rem .9rem',
    fontWeight: 600,
    fontSize: 12,
    color: '#6b7280',
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    marginBottom: '0.9rem',
  },

  /* Layout for digits + labels */
  groupsGrid: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto auto auto',
    justifyContent: 'center',
    alignItems: 'start',
    columnGap: '12px',
  },
  groupCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  digitsRow: {
    display: 'flex',
    gap: '4px',
    alignItems: 'baseline',
    justifyContent: 'center',
  },
  digit: {
    display: 'inline-block',
    fontSize: '2.8rem',
    lineHeight: 1,
    fontWeight: 900,
    letterSpacing: '-0.02em',
    color: '#0f172a',
    fontVariantNumeric: 'tabular-nums',
  },
  digitFaded: { color: '#9ca3af' },

  unitLabel: {
    marginTop: '0.5rem',
    fontSize: 11,
    color: '#6b7280',
    fontWeight: 500,
    letterSpacing: '.02em',
    textTransform: 'uppercase',
  },

  colonCol: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'flex-start',
    fontSize: '2.2rem',
    fontWeight: 800,
    color: '#9ca3af',
    lineHeight: 1,
    transform: 'translateY(6px)',
    marginTop: 0,
  },

  /* Rounds */
  roundsContainer: {
    marginTop: '1.1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '.3rem',
    color: '#374151',
  },
  roundsPill: {
    backgroundColor: '#fff',
    border: '1px solid #e5e7eb',
    border: '1px solid #e5e7eb',
    borderRadius: 999,
    padding: '.4rem 1.1rem',
    fontSize: 12,
  },
  roundsBold: { fontWeight: 600 },
  roundsText: {
    color: '#6b7280',
    fontWeight: 500,
    fontSize: 11,
    letterSpacing: '.01em',
    textTransform: 'uppercase',
    marginTop: 1,
  },
};

export default function MixerPortalHeader({
  userId,
  firstName: fnProp = '',
  eventName = 'Event',
  roundStartTimes = [],
  nextRoundStartsAt = null,
}) {
  const [firstName, setFirstName] = useState(fnProp || null);

  useEffect(() => {
    if (firstName || !userId) return;
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/${userId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` },
        });
        const data = await res.json();
        if (data?.firstName) setFirstName(data.firstName);
      } catch (err) {
        console.error('[MixerPortalHeader] fetch firstName →', err);
      }
    })();
  }, [firstName, userId]);

  const nextRoundDate = useMemo(() => {
    if (nextRoundStartsAt) return new Date(nextRoundStartsAt);
    return roundStartTimes.map((ts) => new Date(ts)).find((d) => d > new Date()) || null;
  }, [nextRoundStartsAt, roundStartTimes]);

  const diffToParts = (target) => {
    if (!target) return { h: '--', m: '--', s: '--' };
    const diff = target - Date.now();
    if (diff <= 0) return { h: '00', m: '00', s: '00' };
    return {
      h: String(Math.floor(diff / 3_600_000)).padStart(2, '0'),
      m: String(Math.floor((diff % 3_600_000) / 60_000)).padStart(2, '0'),
      s: String(Math.floor((diff % 60_000) / 1_000)).padStart(2, '0'),
    };
  };

  const [countdown, setCountdown] = useState(diffToParts(nextRoundDate));
  useEffect(() => {
    setCountdown(diffToParts(nextRoundDate));
    if (!nextRoundDate) return;
    const id = setInterval(() => setCountdown(diffToParts(nextRoundDate)), 1000);
    return () => clearInterval(id);
  }, [nextRoundDate]);

  const currentRound = useMemo(() => {
    const idx = roundStartTimes.findIndex((ts) => new Date(ts) > new Date());
    return idx === -1 ? roundStartTimes.length : idx + 1;
  }, [roundStartTimes]);

  const hoursFaded = countdown.h === '00';
  const minutesFaded = countdown.m === '00';

  return (
    <div style={styles.container}>
      {/* Headline (match RevealIntroWizardPage) */}
      <div style={styles.headerWrap}>
        <div style={styles.headerTop}>
          {`WELCOME ${firstName ? firstName.toUpperCase() : '…'} TO`}
        </div>
        <div style={styles.headerBottom}>{eventName}</div>
        <p style={styles.subheading}>
          Relax and mingle, but watch the round start time—you don’t want to miss it.
        </p>
      </div>

      {/* Countdown */}
      <div style={styles.timerContainer}>
        <div style={styles.timerPill}>Next round in</div>

        <div style={styles.groupsGrid} aria-live="polite">
          {/* Hours */}
          <div style={styles.groupCol}>
            <div style={styles.digitsRow}>
              <span style={{ ...styles.digit, ...(hoursFaded ? styles.digitFaded : {}) }}>
                {countdown.h[0]}
              </span>
              <span style={{ ...styles.digit, ...(hoursFaded ? styles.digitFaded : {}) }}>
                {countdown.h[1]}
              </span>
            </div>
            <div style={styles.unitLabel}>Hours</div>
          </div>

          <div style={styles.colonCol}>:</div>

          {/* Minutes */}
          <div style={styles.groupCol}>
            <div style={styles.digitsRow}>
              <span style={{ ...styles.digit, ...(minutesFaded ? styles.digitFaded : {}) }}>
                {countdown.m[0]}
              </span>
              <span style={{ ...styles.digit, ...(minutesFaded ? styles.digitFaded : {}) }}>
                {countdown.m[1]}
              </span>
            </div>
            <div style={styles.unitLabel}>Minutes</div>
          </div>

          <div style={styles.colonCol}>:</div>

          {/* Seconds */}
          <div style={styles.groupCol}>
            <div style={styles.digitsRow}>
              <span style={styles.digit}>{countdown.s[0]}</span>
              <span style={styles.digit}>{countdown.s[1]}</span>
            </div>
            <div style={styles.unitLabel}>Seconds</div>
          </div>
        </div>
      </div>

      {/* Rounds */}
      <div style={styles.roundsContainer}>
        <div style={styles.roundsPill}>
          <span style={styles.roundsBold}>{currentRound}</span> / {roundStartTimes.length}
        </div>
        <div style={styles.roundsText}>Rounds</div>
      </div>
    </div>
  );
}
